// What the case inbox (lib/caseInbox.js) does with a case: keeps it in the
// investigation store under INBOX_SCOPE (so the welcome page and every
// workspace read the same record), digs it with one investigator through
// that scope's swarm (within its spend ceiling, which the reader sees and
// raises), records the reader's decision, opens it as a workspace, and runs
// audits. Every page showing the inbox says what it read
// (provideInboxContext), for what investigators read after a dig.
import { INBOX_SCOPE, auditPicks } from "./caseInbox.js";
import { caseView, groupsOf } from "./investigateCases.js";
import { SET_ASIDE } from "./investigateFacts.js";
import { investigationOf, putCases, updateCase } from "./investigateStore.js";
import { dispatch, provideInvestigateContext, startSwarm, swarmOf } from "./investigateSwarm.js";
import { openCaseOnWorkspace } from "./investigateWorkspace.js";
import { browserStorageKey } from "./browserStorageScope.js";

const str = (v) => (v == null ? "" : String(v));
const lower = (v) => str(v).trim().toLowerCase();

const storedCase = (id) => investigationOf(INBOX_SCOPE).cases.find((c) => c.id === id) || null;

/**
 * Keeps a formed case as it stands, under its id: the findings it holds
 * now, added to any it held before, and how it came to need someone.
 * `extra` is written over it (an audit, a decision). A finding another case
 * the reader kept holds stays with that case, whose dig and decision speak
 * for it; one an audit sampled stays in the audit's record as well
 * (investigateStore). Returns its id.
 */
export function keepInboxCase(view, extra = {}) {
    const had = storedCase(view.id);
    const elsewhere = new Set(investigationOf(INBOX_SCOPE).cases.filter((c) => c.id !== view.id && !c.audit).flatMap((c) => c.findings));
    const findings = [...new Set([...(had?.findings || []), ...(view.findingIds || []).map(lower).filter((id) => !elsewhere.has(id))])];
    // Every one is another's: that case went to another formed case, and
    // this one takes them.
    if (!findings.length) findings.push(...new Set((view.findingIds || []).map(lower)));
    const origin = view.origin && view.origin !== "kept" ? view.origin : had?.origin;
    const kinds = [...new Set([...(had?.kinds || []), ...(view.kinds || [])])];
    if (had && findings.length === had.findings.length && origin === had.origin && kinds.length === (had.kinds || []).length && !Object.keys(extra).length) return view.id;
    putCases(INBOX_SCOPE, [{ ...(had || {}), id: view.id, title: view.title, findings, by: had?.by || "investigate", ...(origin ? { origin } : {}), ...(kinds.length ? { kinds } : {}), ...extra, at: Date.now() }]);
    return view.id;
}

const ensureOn = () => {
    if (!swarmOf(INBOX_SCOPE)?.on) startSwarm(INBOX_SCOPE);
};

// One investigator on the case: a set-aside one is double-checked. It reads
// the sessions in `window`, names them by `nameOf`, and pins what it notes
// on them. `leads`: sessions near the case it may read and name as part of
// its story ({ session, why }), ahead of those formation found near it.
const digItem = (view, { question = "", window = null, nameOf = null, leads = [] } = {}) => ({
    kind: SET_ASIDE.has(view.group) ? "double_check" : "dig",
    caseId: view.id, findingIds: view.findingIds, question,
    view: leads.length ? { ...view, leads: [...leads, ...(view.nearby || [])] } : view,
    window, nameOf, pin: true,
});

/** Digs a case, kept first. Returns the investigators queued. */
export function digInboxCase(view, { question = "", window = null, nameOf = null, leads = [] } = {}) {
    const id = keepInboxCase(view);
    ensureOn();
    return dispatch(INBOX_SCOPE, [digItem({ ...view, id }, { question, window, nameOf, leads })], { by: "you", front: true });
}

/** The reader's decision on a case (let_go | taken), or null to take it back. */
export function decideInboxCase(view, kind) {
    const id = keepInboxCase(view);
    updateCase(INBOX_SCOPE, id, (c) => ({ ...c, decision: kind ? { kind, at: Date.now() } : null }));
}

/**
 * Opens a case as a workspace (or only keeps its board, `open` false):
 * anchored on its lead finding's session, its sessions laid on, its findings
 * pinned open, what its digs found pinned at their moments.
 */
export function openInboxCase(view, { investigationId = null, titleOf = null, open = true, focus = null } = {}) {
    const id = keepInboxCase(view);
    const kept = storedCase(id);
    return openCaseOnWorkspace({
        scopeKey: INBOX_SCOPE,
        view: { ...view, id, account: kept?.account || view.account, dig: kept?.dig || view.dig, notes: kept?.notes || view.notes, related: kept?.related || view.related },
        titleOf, open, focus, investigationId,
    });
}

const AUDIT_QUESTION = "No standard escalated this. Should a person have seen it? Answer needs_person for that question.";

/**
 * What an audit sends: a random few of each group no case holds
 * (auditPicks), and the rare acts nobody has dug, each kept as a case marked
 * with how it was picked. `salt` picks the sample. Returns dispatch items.
 */
export function auditItems({ placed = [], map = null, cases = [], window = null, nameOf = null, salt = String(Date.now()) } = {}) {
    const picks = auditPicks(placed, { map, investigation: investigationOf(INBOX_SCOPE), cases, salt });
    const at = Date.now();
    const items = [];
    for (const pick of picks.random) {
        const id = `a:${lower(pick.items[0].finding.id)}`;
        const view = caseView({ id, title: "", notes: [], related: [], matches: [] }, pick.items);
        putCases(INBOX_SCOPE, [{ id, title: view.title, findings: view.findingIds, by: "investigate", origin: "audit", audit: { kind: "random", group: pick.group, at }, at }]);
        items.push(digItem(view, { question: AUDIT_QUESTION, window, nameOf }));
    }
    for (const c of picks.rare) {
        const id = keepInboxCase(c, { audit: { kind: "rare", group: c.group, at } });
        items.push(digItem({ ...c, id }, { window, nameOf }));
    }
    return items;
}

/** Audits what the standards let through (auditItems). Returns the investigators queued. */
export function auditInbox(args = {}) {
    const items = auditItems(args);
    if (!items.length) return [];
    ensureOn();
    return dispatch(INBOX_SCOPE, items, { by: "you" });
}

// What a Start sends with no chat to coordinate it: a dig of every case
// nobody has dug or decided, the most pressing first.
export const WAVE_MAX = 24;
export function inboxWave(built, { window = null, nameOf = null } = {}) {
    return built.cases.filter((c) => !c.dig && !c.decision).slice(0, WAVE_MAX).map((c) => digItem(c, { window, nameOf }));
}

// The inbox's cases as a scope's are built (investigateCases buildCases).
export function builtOf(cases) {
    const caseOf = new Map();
    for (const c of cases) for (const id of c.findingIds) if (!caseOf.has(id)) caseOf.set(id, c.id);
    return { cases, byId: new Map(cases.map((c) => [c.id, c])), caseOf, groups: groupsOf(cases), pending: {} };
}

// -- what is new since the reader last looked -----------------------------------

// When the reader last looked at the cases (their previous visit), for
// marking what is new since: read once per page and per reader, this visit
// kept for the next. Null before a first visit, or where the browser keeps
// nothing for a reader.
const SEEN_PREFIX = "origin-inbox-seen-v1";
let visit = null;
export function lastLookedAt(nowMs = Date.now()) {
    const key = browserStorageKey(SEEN_PREFIX);
    if (!key) return null;
    if (visit?.key === key) return visit.before;
    let before = null;
    try { before = Number(globalThis.localStorage?.getItem(key)) || null; } catch { before = null; }
    try { globalThis.localStorage?.setItem(key, String(nowMs)); } catch { /* not kept: the next visit reads as a first */ }
    visit = { key, before };
    return before;
}

// Test seam: a new page.
export function _resetLastLookedForTests() {
    visit = null;
}

// -- what investigators read -------------------------------------------------------

// Each page showing the inbox says what it read ({ pool, map, verdicts,
// standards, window, nameOf }) and the cases it formed from it (`cases`;
// `primary` for the welcome page's); investigators read them together (the
// swarm keeps one context a scope), so the standards asked after a dig see
// every finding in view anywhere, and the coordinator reads the cases the
// reader sees: the welcome page's, else the latest workspace's.
const hosts = new Map();
export function provideInboxContext(hostId, ctx) {
    if (ctx) hosts.set(hostId, ctx);
    else hosts.delete(hostId);
    // With no page left, the last context stays for what is still at work.
    if (hosts.size) provideInvestigateContext(INBOX_SCOPE, mergeContexts([...hosts.values()]));
}

const firstOf = (list, fn) => {
    for (const c of list) {
        const v = fn(c);
        if (v) return v;
    }
    return null;
};

export function mergeContexts(list) {
    const pool = new Map();
    const findings = new Map();
    const verdicts = {};
    for (const c of list) {
        for (const f of c.pool || []) if (!pool.has(f.id)) pool.set(f.id, f);
        for (const [k, f] of c.map?.findings || []) if (!findings.has(k)) findings.set(k, f);
        Object.assign(verdicts, c.verdicts || {});
    }
    const windows = list.map((c) => c.window).filter((w) => Number.isFinite(w?.fromMs) && Number.isFinite(w?.toMs));
    const window = windows.length ? { fromMs: Math.min(...windows.map((w) => w.fromMs)), toMs: Math.max(...windows.map((w) => w.toMs)) } : null;
    const nameOf = {
        session: (id) => firstOf(list, (c) => c.nameOf?.session?.(id)),
        machine: (endpoint) => firstOf(list, (c) => c.nameOf?.machine?.(endpoint)),
        conduct: (f) => firstOf(list, (c) => c.nameOf?.conduct?.(f)),
    };
    const primary = list.find((c) => c.primary) || list[list.length - 1] || null;
    return {
        pool: [...pool.values()],
        map: { findings },
        verdicts,
        standards: firstOf(list, (c) => (c.standards?.length ? c.standards : null)) || [],
        start: null,
        statuses: {},
        window,
        nameOf,
        scopeLabel: INBOX_LABEL,
        inbox: true,
        pin: true,
        build: () => builtOf(primary?.cases || []),
        keep: (view) => keepInboxCase(view),
        wave: (built) => inboxWave(built, { window, nameOf }),
        auditItems: (snapshot) => auditItems({ placed: snapshot.placed, map: primary?.map || null, cases: snapshot.built.cases, window, nameOf }),
    };
}

// What the inbox's investigation is called in its chat.
export const INBOX_LABEL = "the case inbox";

// Test seam.
export function _resetInboxHostsForTests() {
    hosts.clear();
}
